<script setup>
import { Link, usePage } from '@inertiajs/vue3';
import DagensFund from '@/Components/DagensFund.vue';
import PriceCompareLayout from '@/Layouts/PriceCompareLayout.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import { computed } from 'vue';

const page = usePage();
const articles = computed(() => page.props.articles?.data || []);
const pagination = computed(() => page.props.articles || {});

const valgtSortering = computed(() => page.props.sortering || 'nyeste');

const sorteringsvalg = [
    { vaerdi: 'nyeste', navn: 'Nyeste' },
    { vaerdi: 'aeldste', navn: 'Ældste' },
    { vaerdi: 'laeste', navn: 'Mest læste' },
];

/*
 * Sidetal omkring den man står på, ikke dem alle sammen.
 *
 * Der blev tegnet et link pr. side. Med ni artikler var det een knap, men
 * bagkataloget fylder listen op, og ved 500 artikler bliver det 42 tal der
 * på en telefon står som en mur. Vinduet viser den første, den sidste, og
 * to til hver side af den man er på.
 */
const sidetal = computed(() => {
    const sidste = pagination.value?.last_page ?? 1;
    const nu = pagination.value?.current_page ?? 1;

    if (sidste <= 7) {
        return Array.from({ length: sidste }, (_, i) => i + 1);
    }

    const ud = new Set([1, sidste, nu]);

    for (let i = 1; i <= 2; i++) {
        if (nu - i >= 1) ud.add(nu - i);
        if (nu + i <= sidste) ud.add(nu + i);
    }

    const tal = [...ud].sort((a, b) => a - b);
    const med = [];

    tal.forEach((n, i) => {
        if (i > 0 && n - tal[i - 1] > 1) med.push('...');
        med.push(n);
    });

    return med;
});
const breadcrumbs = page.props.breadcrumbs;
</script>

<template>
    <PriceCompareLayout>
        <div class="container-site art-page">
            <BreadcrumbBar :breadcrumbs="breadcrumbs" class="mb-4" />

            <div class="art-layout">
                <!-- Main column -->
                <div class="art-main">
                    <!-- Hero -->
                    <section class="art-hero">
                        <h1 class="art-hero__title">Artikler</h1>
                        <p class="art-hero__desc">
                            Læs vores seneste guides, tests og nyheder om produkter, teknologi og prisudvikling i Danmark.
                        </p>
                    </section>

                    <div v-if="pagination.total > 1" class="art-sort">
                        <span class="art-sort__antal">{{ pagination.total }} artikler</span>
                        <div class="art-sort__valg" role="group" aria-label="Sortering">
                            <Link
                                v-for="v in sorteringsvalg"
                                :key="v.vaerdi"
                                :href="route('articles', v.vaerdi === 'nyeste' ? {} : { sorter: v.vaerdi })"
                                preserve-scroll
                                class="art-sort__knap"
                                :class="{ 'art-sort__knap--valgt': v.vaerdi === valgtSortering }"
                                :aria-current="v.vaerdi === valgtSortering ? 'true' : undefined"
                            >{{ v.navn }}</Link>
                        </div>
                    </div>

                    <!-- Article grid -->
                    <div class="art-grid">
                        <Link
                            v-for="article in articles"
                            :key="article.id"
                            :href="route('articles.show', article.name_url_friendly)"
                            class="art-card"
                        >
                            <div class="art-card__img-wrap">
                                <img
                                    v-if="article.cover_image"
                                    :src="route('getArticleImage', article.cover_image)"
                                    :alt="article.name"
                                    class="art-card__img"
                                    loading="lazy"
                                />
                                <div v-else class="art-card__img-placeholder">
                                    <i class="bi bi-file-earmark-text"></i>
                                </div>
                            </div>
                            <div class="art-card__body">
                                <div class="art-card__meta">
                                    <span class="art-card__badge">{{ article.category || 'Artikel' }}</span>
                                    <span class="art-card__date">{{ new Date(article.post_date).toLocaleDateString('da-DK') }}</span>
                                </div>
                                <h3 class="art-card__title">{{ article.name }}</h3>
                                <p class="art-card__teaser">
                                    {{ article.teaser_text || (article.full_text ? article.full_text.slice(0, 120) + '...' : '') }}
                                </p>
                                <span class="art-card__cta">
                                    Læs artikel <i class="bi bi-arrow-right ms-1"></i>
                                </span>
                            </div>
                        </Link>
                    </div>

                    <!-- Pagination -->
                    <nav v-if="pagination.last_page > 1" class="art-pagination" aria-label="Sidenavigation">
                        <Link
                            v-if="pagination.prev_page_url"
                            :href="pagination.prev_page_url"
                            class="art-pagination__btn"
                        >Forrige</Link>
                        <span v-else class="art-pagination__btn art-pagination__btn--disabled">Forrige</span>

                        <template v-for="(pageNumber, i) in sidetal" :key="`s-${i}`">
                            <span v-if="pageNumber === '...'" class="art-pagination__num art-pagination__num--spring">...</span>
                            <Link
                                v-else
                                :href="route('articles', { page: pageNumber })"
                                class="art-pagination__num"
                                :class="{ 'art-pagination__num--active': pageNumber === pagination.current_page }"
                            >{{ pageNumber }}</Link>
                        </template>

                        <Link
                            v-if="pagination.next_page_url"
                            :href="pagination.next_page_url"
                            class="art-pagination__btn"
                        >Næste</Link>
                        <span v-else class="art-pagination__btn art-pagination__btn--disabled">Næste</span>
                    </nav>
                </div>

                <!-- Sidebar -->
                <aside class="art-sidebar">
                    <!-- Var en tom kasse med "300 x 250". -->
                    <div class="art-sidebar__card" v-if="$page.props.dagensFund?.length">
                        <DagensFund :fund="$page.props.dagensFund" :interval="$page.props.fundRotationMs || 6000" />
                    </div>

                    <!-- Latest news -->
                    <div class="art-sidebar__card" v-if="articles.length">
                        <h4 class="art-sidebar__heading">Seneste nyheder</h4>
                        <div
                            v-for="article in articles.slice(0, 4)"
                            :key="'side-' + article.id"
                            class="art-sidebar__news-item"
                        >
                            <span class="art-sidebar__news-date">{{ new Date(article.post_date).toLocaleDateString('da-DK') }}</span>
                            <Link :href="route('articles.show', article.name_url_friendly)" class="art-sidebar__news-link">
                                {{ article.name }}
                            </Link>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    </PriceCompareLayout>
</template>

<style scoped>
.art-page {
    padding-top: 1rem;
    padding-bottom: 3rem;
}

/* ── Layout ── */
.art-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
}

@media (min-width: 992px) {
    .art-layout {
        grid-template-columns: 1fr 280px;
    }
}

/* ── Hero ── */
.art-hero {
    background: var(--pc-bg-secondary);
    border: 1px solid var(--pc-border);
    border-left: 3px solid var(--pc-accent);
    border-radius: var(--pc-radius-lg);
    padding: 2rem 2rem;
    margin-bottom: 2rem;
}

.art-hero__title {
    font-size: var(--pc-text-2xl);
    font-weight: 700;
    color: var(--pc-text-primary);
    margin: 0 0 0.5rem;
}

.art-hero__desc {
    font-size: var(--pc-text-base);
    color: var(--pc-text-secondary);
    margin: 0;
    max-width: 700px;
}

/* ── Article grid ── */
.art-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2.5rem;
}

@media (min-width: 576px) {
    .art-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 992px) {
    .art-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

/* ── Article card ── */
.art-card {
    background: var(--pc-bg-secondary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-lg);
    overflow: hidden;
    text-decoration: none;
    color: var(--pc-text-primary);
    display: flex;
    flex-direction: column;
    transition: transform var(--pc-transition), box-shadow var(--pc-transition);
}

.art-card:hover {
    transform: translateY(-3px);
    box-shadow: var(--pc-shadow-lg);
    color: var(--pc-text-primary);
    text-decoration: none;
}

.art-card__img-wrap {
    width: 100%;
    aspect-ratio: 16 / 10;
    background: var(--pc-bg-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.art-card__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.art-card__img-placeholder {
    font-size: 3rem;
    color: var(--pc-text-muted);
}

.art-card__body {
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 0.5rem;
}

.art-card__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.art-card__badge {
    background: var(--pc-accent-light);
    color: var(--pc-accent);
    font-size: var(--pc-text-xs);
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: var(--pc-radius-full);
    border: 1px solid var(--pc-accent);
}

.art-card__date {
    font-size: var(--pc-text-xs);
    color: var(--pc-text-muted);
}

.art-card__title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--pc-text-primary);
    margin: 0;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.art-card__teaser {
    font-size: var(--pc-text-sm);
    color: var(--pc-text-secondary);
    margin: 0;
    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.art-card__cta {
    font-size: var(--pc-text-sm);
    font-weight: 600;
    color: var(--pc-accent);
    margin-top: auto;
    transition: color var(--pc-transition);
}

.art-card:hover .art-card__cta {
    color: var(--pc-accent-hover);
}

/* ── Sortering ── */
.art-sort {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    margin-bottom: 1.25rem;
}

.art-sort__antal {
    font-size: var(--pc-text-sm);
    color: var(--pc-text-muted);
}

.art-sort__valg {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.art-sort__knap {
    padding: 0.4rem 0.85rem;
    font-size: var(--pc-text-sm);
    font-weight: 600;
    border-radius: var(--pc-radius);
    border: 1px solid var(--pc-border);
    background: var(--pc-bg-secondary);
    color: var(--pc-text-primary);
    text-decoration: none;
    transition: all var(--pc-transition);
}

.art-sort__knap:hover {
    border-color: var(--pc-primary);
}

.art-sort__knap--valgt {
    background: var(--pc-primary);
    border-color: var(--pc-primary);
    color: #fff;
}

/* Paa en telefon skal valgene have hele bredden og staa under antallet. */
@media (max-width: 480px) {
    .art-sort {
        flex-direction: column;
        align-items: stretch;
    }

    .art-sort__valg {
        justify-content: space-between;
    }

    .art-sort__knap {
        flex: 1;
        text-align: center;
    }
}

/* ── Pagination ── */
.art-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
}

.art-pagination__btn,
.art-pagination__num {
    padding: 0.45rem 0.9rem;
    font-size: var(--pc-text-sm);
    font-weight: 600;
    border-radius: var(--pc-radius);
    border: 1px solid var(--pc-border);
    background: var(--pc-bg-secondary);
    color: var(--pc-text-primary);
    text-decoration: none;
    transition: all var(--pc-transition);
}

.art-pagination__btn:hover,
.art-pagination__num:hover {
    background: var(--pc-bg-hover);
    border-color: var(--pc-accent);
    color: var(--pc-accent);
    text-decoration: none;
}

.art-pagination__num--spring {
    border-color: transparent;
    background: transparent;
    color: var(--pc-text-muted);
    cursor: default;
    padding-inline: 0.35rem;
}

.art-pagination__num--active {
    background: var(--pc-accent);
    border-color: var(--pc-accent);
    color: var(--pc-bg-primary);
}

.art-pagination__num--active:hover {
    background: var(--pc-accent-hover);
    color: var(--pc-bg-primary);
}

.art-pagination__btn--disabled {
    opacity: 0.4;
    pointer-events: none;
}

/* ── Sidebar ── */
.art-sidebar {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.art-sidebar__card {
    background: var(--pc-bg-secondary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-lg);
    padding: 1.25rem;
}

.art-sidebar__ad-label {
    display: block;
    font-size: var(--pc-text-sm);
    font-weight: 600;
    color: var(--pc-text-secondary);
    margin-bottom: 0.75rem;
    text-align: center;
}

.art-sidebar__ad-box {
    background: var(--pc-bg-tertiary);
    border: 1px dashed var(--pc-border);
    border-radius: var(--pc-radius);
    padding: 3rem 1rem;
    text-align: center;
    color: var(--pc-text-muted);
    font-size: var(--pc-text-sm);
}

.art-sidebar__heading {
    font-size: var(--pc-text-lg);
    font-weight: 600;
    color: var(--pc-text-primary);
    margin: 0 0 1rem;
}

.art-sidebar__news-item {
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--pc-border);
}

.art-sidebar__news-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.art-sidebar__news-date {
    display: block;
    font-size: var(--pc-text-xs);
    color: var(--pc-text-muted);
    margin-bottom: 0.25rem;
}

.art-sidebar__news-link {
    font-size: var(--pc-text-sm);
    color: var(--pc-text-primary);
    text-decoration: none;
    line-height: 1.4;
    transition: color var(--pc-transition);
}

.art-sidebar__news-link:hover {
    color: var(--pc-accent);
}

/* ── Responsive ── */
@media (max-width: 575px) {
    .art-hero {
        padding: 1.5rem;
    }

    .art-hero__title {
        font-size: var(--pc-text-xl);
    }
}
</style>
